import React, { useContext, useState } from 'react'
import classNames from 'classnames'
import { useIntl } from 'react-intl'
import { AdminToast, AdminCheckbox } from '@project/components'
import PageContentContainer from '../../components/PageContentContainer/PageContentContainer'
import Title from '../../components/Title/Title'
import TitleDescription from '../../components/TitleDescription/TitleDescription'
import ButtonWithDescTwoColumn from '../../components/ButtonWithDescTwoColumn/ButtonWithDescTwoColumn'
import { RootContext } from '../../hoc/RootContext/RootContext'
import styles from './NotificationPreferences.module.css'
import { axiosV2 } from '../../utils/axios'

const NotificationPreferences = () => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const { currentUser } = useContext(RootContext);
  const { notificationSettings = {} } = currentUser || {};
  const [inspectionAssigned, setInspectionAssigned] = useState(notificationSettings?.inspectionAssigned ?? true);
  const [inspectionWatcher, setInspectionWatcher] = useState(notificationSettings?.inspectionWatcher ?? true);
  const [workOrderStatusUpdate, setWorkOrderStatusUpdate] = useState(notificationSettings?.workOrderStatusUpdate ?? true);
  const [workOrderWatcher, setWorkOrderWatcher] = useState(notificationSettings?.workOrderWatcher ?? true);
  const [workOrderCommentMention, setWorkOrderCommentMention] = useState(notificationSettings?.workOrderCommentMention ?? true);
  const [workOrderCommentAdded, setWorkOrderCommentAdded] = useState(notificationSettings?.workOrderCommentAdded ?? true);

  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  const handleToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const onSave = async () => {
    if (isSaving) return;

    setIsSaving(true);

    try {

      const body = {
        notificationSettings: {
          inspectionAssigned,
          inspectionWatcher,
          workOrderStatusUpdate,
          workOrderWatcher,
          workOrderCommentMention,
          workOrderCommentAdded
        }
      }

      const response = await axiosV2.put(`user/${currentUser.username}`, body);

      if (response.status === 200) {
        handleToast(t('Company.companySettings.saved'));
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      handleToast(error.message, 'danger');
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <PageContentContainer>
      <Title title={t('NotificationPreferences.title')} />
      <main className={classNames(styles.settings, 'shadow-sm border row mt-4 mx-auto p-4')}>
        <h5 className={styles.appName}>ID Inspect</h5>
        <hr className={styles.divider} />
        <div className={styles.Save}>
          <ButtonWithDescTwoColumn
            onClick={onSave}
            buttonDescription={t('NotificationPreferences.buttonDescription')}
            buttonText={t('Settings.Save')}
          />
        </div>


        {/* Inspection Settings */}
        <div className={styles.headerContainer}>
          <h4 className='is-main-heading pb-3 mb-3'>{t('NotificationPreferences.inspectionSettingsTitle')}</h4>
          <h4 className='is-main-heading pb-3 mb-3'>{t('NotificationPreferences.inspectionSettingsEmail')}</h4>
        </div>
        <div className={styles.contentContainer}>
          <TitleDescription title={t('NotificationPreferences.inspectionAssignment')} />
          <div className={styles.checkboxContainer}>
            <AdminCheckbox
              id={'inspectionAssigned'}
              label={t('NotificationPreferences.inspectionAssignmentLabel')}
              onChange={() => setInspectionAssigned(!inspectionAssigned)}
              checked={inspectionAssigned}
              customstyle={styles.checkbox}
              removeMargins={true}
              customLabelStyle={styles.label}
            />
          </div>
        </div>
        <div className={styles.contentContainer}>
          <TitleDescription title={t('NotificationPreferences.inspectionWatcher')} />
          <div className={styles.checkboxContainer}>
            <AdminCheckbox
              id={'inspectionWatcher'}
              label={t('NotificationPreferences.inspectionWatcherLabel')}
              onChange={() => setInspectionWatcher(!inspectionWatcher)}
              checked={inspectionWatcher}
              customstyle={styles.checkbox}
              removeMargins={true}
              customLabelStyle={styles.label}
            />
          </div>
        </div>
        
        {/* Work Order Settings */}
        <div className={styles.headerContainer}>
          <h4 className='is-main-heading pb-3 mb-3'>{t('NotificationPreferences.workOrderSettingsTitle')}</h4>
          <h4 className='is-main-heading pb-3 mb-3'>{t('NotificationPreferences.workOrderSettingsEmail')}</h4>
        </div>
        <div className={styles.contentContainer}>
          <TitleDescription title={t('NotificationPreferences.workOrderStatusUpdate')} />
          <div className={styles.checkboxContainer}>
            <AdminCheckbox
              id={'workOrderStatusUpdate'}
              label={t('NotificationPreferences.workOrderStatusUpdateLabel')}
              onChange={() => setWorkOrderStatusUpdate(!workOrderStatusUpdate)}
              checked={workOrderStatusUpdate}
              customstyle={styles.checkbox}
              removeMargins={true}
              customLabelStyle={styles.label}
            />
          </div>
        </div>
        <div className={styles.contentContainer}>
          <TitleDescription title={t('NotificationPreferences.workOrderWatcher')} />
          <div className={styles.checkboxContainer}>
            <AdminCheckbox
              id={'workOrderWatcher'}
              label={t('NotificationPreferences.workOrderWatcherLabel')}
              onChange={() => setWorkOrderWatcher(!workOrderWatcher)}
              checked={workOrderWatcher}
              customstyle={styles.checkbox}
              removeMargins={true}
              customLabelStyle={styles.label}
            />
          </div>
        </div>
        <div className={styles.contentContainer}>
          <TitleDescription title={t('NotificationPreferences.workOrderCommentMention')} />
          <div className={styles.checkboxContainer}>
            <AdminCheckbox
              id={'workOrderCommentMention'}
              label={t('NotificationPreferences.workOrderCommentMentionLabel')}
              onChange={() => setWorkOrderCommentMention(!workOrderCommentMention)}
              checked={workOrderCommentMention}
              customstyle={styles.checkbox}
              removeMargins={true}
              customLabelStyle={styles.label}
            />
          </div>
        </div>
        <div className={styles.contentContainer}>
          <TitleDescription title={t('NotificationPreferences.workOrderCommentAdded')} />
          <div className={styles.checkboxContainer}>
            <AdminCheckbox
              id={'workOrderCommentAdded'}
              label={t('NotificationPreferences.workOrderCommentAddedLabel')}
              onChange={() => setWorkOrderCommentAdded(!workOrderCommentAdded)}
              checked={workOrderCommentAdded}
              customstyle={styles.checkbox}
              removeMargins={true}
              customLabelStyle={styles.label}
            />
          </div>
        </div>
        <AdminToast 
          show={showToast}
          message={toastMessage}
          onClose={() => setShowToast(false)}
          style={{ top: '0px', right: '70px' }}
          variant={toastVariant}
        />
      </main>
    </PageContentContainer>
  );
};

export default NotificationPreferences;
